import { dateInRange } from 'shared';

import { Column, FilterState, Row } from './types';

export function filterRows<T extends Row>({
  filterState,
  rows,
  columns
}: {
  filterState: FilterState[];
  rows: T[];
  columns: Column<T>[];
}) {
  if (filterState.length === 0) {
    return rows;
  }

  const filters = filterState.map(filter => ({ ...filter, substring: filter.substring?.toLowerCase() }));

  // Group filters by key
  const filtersByKey = filters.reduce((acc, filter) => {
    if (!acc[filter.key]) {
      acc[filter.key] = [];
    }
    acc[filter.key].push(filter);
    return acc;
  }, {} as Record<string, typeof filters>);

  return rows.filter(row => {
    // For each column, check if the row matches any of its filters
    return Object.entries(filtersByKey).every(([key, columnFilters]) => {
      const column = columns.find(c => c.key === key);
      if (!column) return true;

      const rawValue = row[key as keyof T];
      const value = column?.filterValue
        ? column?.filterValue(row)
        : typeof rawValue === 'number'
        ? rawValue.toString()
        : rawValue;

      // A row matches if it satisfies ANY of the filters for this column
      return columnFilters.some(({ substring, oneOf, startDate, endDate, minValue, maxValue }) => {
        const fn = column?.filters?.textSearch?.fn;

        if (fn && substring) {
          return fn(row, substring);
        }

        if (substring) {
          if (typeof value !== 'string') return false;
          return value.toLowerCase().includes(substring);
        } else if (oneOf) {
          if (column.filters?.oneOf?.defaultOption.anyFilterOption.value === oneOf[0]) {
            return true;
          }
          if (Array.isArray(value)) {
            return value.some(v => oneOf.includes(v));
          }

          // @ts-ignore
          return oneOf.includes(value);
        } else if (startDate || endDate) {
          if (typeof value !== 'string') return false;
          return dateInRange(new Date(value), startDate, endDate);
        } else if (minValue !== undefined || maxValue !== undefined) {
          if (minValue !== undefined && maxValue !== undefined) {
            return Number(value) >= Number(minValue) && Number(value) <= Number(maxValue);
          } else {
            return minValue !== undefined
              ? Number(value) >= Number(minValue)
              : true && maxValue !== undefined
              ? Number(value) <= Number(maxValue)
              : true;
          }
        }

        return false;
      });
    });
  });
}
